import * as React from "react";
import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from "@/components/ui/field";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { ComposeOutputMode, InjectReadiness, PineParseIssue } from "../../types/reportStrategyPine";
import { groupParseIssuesByKind } from "../../utils/pineIrUi";
import {
  injectReadinessPineBadgeItem,
  PineBadgeList,
  scriptKindPineBadgeItem,
} from "../../views/Dashboard/Strategies/strategyTablePineBadges";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "./StrategyPineSectionCard";

/** Prevent FieldLabel has-data-checked primary tint stacking under alert-matched card fill. */
const COMPOSE_MODE_CHOICE_LABEL_CLASS =
  "has-data-checked:!border-transparent has-data-checked:!bg-transparent dark:has-data-checked:!border-transparent dark:has-data-checked:!bg-transparent";

export type StrategyComposeModePickerProps = {
  isIndicatorScript: boolean;
  mergeAllowed: boolean;
  mergeReason: string | null;
  outputMode: ComposeOutputMode;
  onOutputModeChange: (mode: ComposeOutputMode) => void;
  mergeModeLabel: string;
  mergeModeDescription: string;
  appendModeLabel: string;
  appendModeDescription: string;
  separateModeLabel: string;
  separateModeDescription: string;
  parseIssues: PineParseIssue[];
  scriptKind?: string | null;
  injectReadiness?: InjectReadiness | string | null;
  /** Script kind + inject readiness chips above mode radios (hidden in embedded checklist compose). */
  showContextBadges?: boolean;
};

export function StrategyComposeModePicker({
  isIndicatorScript,
  mergeAllowed,
  mergeReason,
  outputMode,
  onOutputModeChange,
  mergeModeLabel,
  mergeModeDescription,
  appendModeLabel,
  appendModeDescription,
  separateModeLabel,
  separateModeDescription,
  parseIssues,
  scriptKind,
  injectReadiness,
  showContextBadges = true,
}: StrategyComposeModePickerProps): React.ReactElement {
  const grouped = groupParseIssuesByKind(parseIssues);
  const showMerge = !isIndicatorScript && mergeAllowed;
  const showMergeBlocked = !isIndicatorScript && !mergeAllowed;
  const contextBadges = useMemo(() => {
    const items = [];
    const kindBadge = scriptKindPineBadgeItem(scriptKind);
    if (kindBadge) items.push(kindBadge);
    if (injectReadiness) {
      const injectBadge = injectReadinessPineBadgeItem(injectReadiness);
      if (injectBadge) items.push(injectBadge);
    }
    return items;
  }, [injectReadiness, scriptKind]);

  return (
    <div className="w-full min-w-0" data-strategy-compose-mode-picker="">
      {showContextBadges && contextBadges.length > 0 ? (
        <div className="mb-3">
          <PineBadgeList items={contextBadges} />
        </div>
      ) : null}

      <Field data-strategy-compose-mode-field="">
        <FieldLabel
          id="strategy-compose-mode-label"
          data-strategy-compose-mode-heading=""
          className={STRATEGY_PINE_SECTION_LABEL_CLASS}
        >
          How should Tessera attach policy?
        </FieldLabel>

        {showMergeBlocked && grouped.blocksMerge.length > 0 ? (
          <Alert variant="warning" data-strategy-compose-merge-blocked="">
            <AlertTriangle aria-hidden />
            <AlertTitle className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Merge is not available</AlertTitle>
            <AlertDescription>
              <ul className="list-disc space-y-1 pl-4 font-[family-name:var(--font-primary)] text-sm text-gray-500 dark:text-white">
                {grouped.blocksMerge.slice(0, 4).map((issue, i) => (
                  <li key={`${issue.code}-${i}`}>
                    {issue.title ?? issue.code}: {issue.message}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        ) : showMergeBlocked && mergeReason ? (
          <Alert variant="warning" data-strategy-compose-merge-blocked="">
            <AlertTriangle aria-hidden />
            <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>{mergeReason}</AlertDescription>
          </Alert>
        ) : null}

        <RadioGroup
          value={outputMode}
          onValueChange={(value) => onOutputModeChange(value as ComposeOutputMode)}
          aria-labelledby="strategy-compose-mode-label"
          className="w-full gap-3"
          data-strategy-compose-mode-radio-group=""
        >
        {showMerge ? (
          <FieldLabel htmlFor="strategy-compose-mode-merge" className={COMPOSE_MODE_CHOICE_LABEL_CLASS}>
            <Field orientation="horizontal" data-strategy-compose-mode-option="merge_into_strategy">
              <FieldContent>
                <FieldTitle className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{mergeModeLabel}</FieldTitle>
                <FieldDescription>{mergeModeDescription}</FieldDescription>
              </FieldContent>
              <RadioGroupItem
                value="merge_into_strategy"
                id="strategy-compose-mode-merge"
                data-strategy-compose-mode-radio=""
                className="mt-0.5"
              />
            </Field>
          </FieldLabel>
        ) : null}

        {isIndicatorScript ? (
          <FieldLabel htmlFor="strategy-compose-mode-append" className={COMPOSE_MODE_CHOICE_LABEL_CLASS}>
            <Field orientation="horizontal" data-strategy-compose-mode-option="append_policy_to_indicator">
              <FieldContent>
                <FieldTitle>{appendModeLabel}</FieldTitle>
                <FieldDescription>{appendModeDescription}</FieldDescription>
              </FieldContent>
              <RadioGroupItem
                value="append_policy_to_indicator"
                id="strategy-compose-mode-append"
                data-strategy-compose-mode-radio=""
                className="mt-0.5"
              />
            </Field>
          </FieldLabel>
        ) : null}

        <FieldLabel htmlFor="strategy-compose-mode-separate" className={COMPOSE_MODE_CHOICE_LABEL_CLASS}>
          <Field orientation="horizontal" data-strategy-compose-mode-option="separate_indicator">
            <FieldContent>
              <FieldTitle className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
                {separateModeLabel}
                {isIndicatorScript ? (
                  <span className="ml-2 font-normal text-muted-foreground">(alternate)</span>
                ) : null}
              </FieldTitle>
              <FieldDescription>{separateModeDescription}</FieldDescription>
            </FieldContent>
            <RadioGroupItem
              value="separate_indicator"
              id="strategy-compose-mode-separate"
              data-strategy-compose-mode-radio=""
              className="mt-0.5"
            />
          </Field>
        </FieldLabel>
        </RadioGroup>
      </Field>
    </div>
  );
}
